import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const THEMES = [
  { id: "aurora", name: "Aurora", hue: 0, sat: 1, swatch: "#00F0FF" },
  { id: "verdant", name: "Verdant", hue: 300, sat: 1.05, swatch: "#12E39A" },
  { id: "amethyst", name: "Amethyst", hue: 90, sat: 1, swatch: "#9D5CFF" },
  { id: "rose", name: "Rose", hue: 130, sat: 1, swatch: "#FF5FB0" },
  { id: "ember", name: "Ember", hue: 200, sat: 1.1, swatch: "#FF7A2D" },
  { id: "mono", name: "Mono", hue: 0, sat: 0, swatch: "#CBD5E1" },
];

export const VOICES = [
  { id: "jarvis", name: "J.A.R.V.I.S.", desc: "Refined British AI" },
  { id: "onyx", name: "Onyx", desc: "Deep · mysterious" },
  { id: "echo", name: "Echo", desc: "Calm · smooth" },
  { id: "ash", name: "Ash", desc: "Clear · articulate" },
  { id: "sage", name: "Sage", desc: "Wise · measured" },
  { id: "fable", name: "Fable", desc: "Expressive · narrator" },
  { id: "nova", name: "Nova", desc: "Bright · energetic" },
  { id: "shimmer", name: "Shimmer", desc: "Light · warm" },
];

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [themeId, setThemeIdState] = useState(() => localStorage.getItem("archon_theme") || "aurora");
  const [voiceId, setVoiceIdState] = useState(() => localStorage.getItem("archon_voice") || "jarvis");
  const syncedRef = useRef(false);

  const theme = THEMES.find((t) => t.id === themeId) || THEMES[0];

  // Pull prefs from the account on login (cross-device sync)
  useEffect(() => {
    if (!user) { syncedRef.current = false; return; }
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/prefs");
        if (!active) return;
        if (data.theme) { setThemeIdState(data.theme); localStorage.setItem("archon_theme", data.theme); }
        if (data.voice) { setVoiceIdState(data.voice); localStorage.setItem("archon_voice", data.voice); }
      } catch { /* */ } finally { syncedRef.current = true; }
    })();
    return () => { active = false; };
  }, [user]);

  const persist = (patch) => {
    if (user && syncedRef.current) { api.put("/prefs", patch).catch(() => {}); }
  };

  const setThemeId = (id) => { setThemeIdState(id); localStorage.setItem("archon_theme", id); persist({ theme: id }); };
  const setVoiceId = (id) => { setVoiceIdState(id); localStorage.setItem("archon_voice", id); persist({ voice: id }); };

  return (
    <ThemeContext.Provider value={{ theme, themeId, setThemeId, voiceId, setVoiceId }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
